// pattern: Imperative Shell
// Flashcard study view — student-facing flip card study interface

import { createElement, addListener, clearElement } from '../shell/dom.ts';
import { createProgress, updateProgress } from '../core/progress-tracker.ts';
import type { FlashcardProgress } from '../core/progress-tracker.ts';
import { saveFlashcardProgress, loadFlashcardProgress } from '../shell/storage.ts';
import type { StoredQuiz } from '../core/question-model.ts';
import type { StoredFlashcard } from '../core/flashcard-model.ts';

type StudyState = {
  cards: ReadonlyArray<StoredFlashcard>;
  currentIndex: number;
  flipped: boolean;
  progressMap: Map<string, FlashcardProgress>;
  sessionCorrect: number;
  sessionIncorrect: number;
};

/**
 * Render flashcard study view. Accepts pre-fetched quiz to avoid double-fetching.
 * Called from quiz-view.ts when quiz.mode === 'flashcard'.
 */
export function renderFlashcardStudy(
  container: HTMLElement,
  quizId: string,
  quiz: StoredQuiz
): () => void {
  const cleanups: Array<() => void> = [];
  const cards = quiz.flashcards ?? [];

  if (cards.length === 0) {
    const emptyDiv = createElement('div', {
      className: 'card text-center empty-state flashcard-empty',
    });

    const nameEl = createElement('h2');
    nameEl.textContent = quiz.name;
    const msgEl = createElement('p', { className: 'text-secondary' });
    msgEl.textContent = 'This flashcard deck has no cards yet.';
    const linkEl = createElement('a', { textContent: 'Go Home', className: 'btn btn-primary' });
    linkEl.setAttribute('href', '#/');

    emptyDiv.append(nameEl, msgEl, linkEl);
    container.appendChild(emptyDiv);

    return () => cleanups.forEach((fn) => fn());
  }

  // Load existing progress
  const progressMap = new Map<string, FlashcardProgress>();
  const saved = loadFlashcardProgress(quizId);
  if (saved.success && saved.data) {
    for (const p of saved.data) {
      progressMap.set(p.cardId, p);
    }
  }

  const state: StudyState = {
    cards,
    currentIndex: 0,
    flipped: false,
    progressMap,
    sessionCorrect: 0,
    sessionIncorrect: 0,
  };

  // Build study shell
  const wrapper = createElement('div', { className: 'flashcard-study-wrapper' });

  const header = createElement('div', { className: 'flashcard-study-header' });
  const titleEl = createElement('h2', {
    textContent: quiz.name,
    className: 'flashcard-study-title',
  });
  const counterEl = createElement('p', { className: 'text-secondary flashcard-counter' });

  header.append(titleEl, counterEl);

  // Progress bar
  const progressBarOuter = createElement('div', { className: 'flashcard-progress-bar' });
  const progressBarFill = createElement('div', { className: 'flashcard-progress-fill' });
  progressBarOuter.appendChild(progressBarFill);

  // Card area
  const cardArea = createElement('div', { className: 'flashcard-container' });

  // Assessment buttons (hidden until flipped)
  const actionsDiv = createElement('div', { className: 'flashcard-actions' });
  actionsDiv.style.display = 'none';

  const knowItBtn = createElement('button', {
    className: 'btn btn-know-it',
    textContent: '\u2713 Know It',
  });
  const needReviewBtn = createElement('button', {
    className: 'btn btn-need-review',
    textContent: '\u2717 Need Review',
  });

  const knowItCleanup = addListener(knowItBtn, 'click', () => handleAssessment(true));
  const needReviewCleanup = addListener(needReviewBtn, 'click', () => handleAssessment(false));
  cleanups.push(knowItCleanup, needReviewCleanup);

  actionsDiv.append(knowItBtn, needReviewBtn);

  wrapper.append(header, progressBarOuter, cardArea, actionsDiv);
  container.appendChild(wrapper);

  let cardCleanups: Array<() => void> = [];
  cleanups.push(() => cardCleanups.forEach((fn) => fn()));

  renderCard();

  function renderCard(): void {
    cardCleanups.forEach((fn) => fn());
    cardCleanups = [];

    state.flipped = false;
    actionsDiv.style.display = 'none';

    const card = state.cards[state.currentIndex]!;
    counterEl.textContent = `Card ${state.currentIndex + 1} of ${state.cards.length}`;
    progressBarFill.style.width = `${(state.currentIndex / state.cards.length) * 100}%`;

    clearElement(cardArea);

    const flashcard = createElement('div', { className: 'flashcard' });

    // Front face
    const front = createElement('div', { className: 'flashcard-face flashcard-front' });
    const frontLabel = createElement('p', {
      className: 'flashcard-face-label',
      textContent: 'Front',
    });
    const frontText = createElement('p', { className: 'flashcard-face-text' });
    frontText.textContent = card.front;

    front.append(frontLabel);

    if (card.figureDataUrl) {
      const figureDiv = createElement('div', { className: 'flashcard-figure' });
      const figureImg = createElement('img') as HTMLImageElement;
      figureImg.src = card.figureDataUrl;
      figureImg.alt = 'Card figure';
      figureDiv.appendChild(figureImg);
      front.appendChild(figureDiv);
    }

    front.appendChild(frontText);

    const tapHint = createElement('p', {
      className: 'flashcard-tap-hint',
      textContent: 'Tap to flip',
    });
    front.appendChild(tapHint);

    // Back face
    const back = createElement('div', { className: 'flashcard-face flashcard-back' });
    const backLabel = createElement('p', {
      className: 'flashcard-face-label',
      textContent: 'Back',
    });
    const backText = createElement('p', { className: 'flashcard-face-text' });
    backText.textContent = card.back;
    back.append(backLabel, backText);

    flashcard.append(front, back);
    cardArea.appendChild(flashcard);

    // Set container height to match front face
    // Use a small delay to let the DOM render first
    setTimeout(() => {
      const frontHeight = front.scrollHeight;
      flashcard.style.minHeight = `${Math.max(frontHeight, 200)}px`;
    }, 0);

    // Click to flip
    const flipCleanup = addListener(flashcard, 'click', () => {
      if (state.flipped) return;
      state.flipped = true;
      flashcard.classList.add('flipped');
      actionsDiv.style.display = 'flex';
    });
    cardCleanups.push(flipCleanup);
  }

  function handleAssessment(correct: boolean): void {
    const card = state.cards[state.currentIndex]!;

    // Update progress
    let progress = state.progressMap.get(card.id) ?? createProgress(card.id);
    progress = updateProgress(progress, correct, Date.now());
    state.progressMap.set(card.id, progress);

    if (correct) {
      state.sessionCorrect++;
    } else {
      state.sessionIncorrect++;
    }

    // Persist progress
    saveFlashcardProgress(quizId, Array.from(state.progressMap.values()));

    // Advance or show summary
    if (state.currentIndex < state.cards.length - 1) {
      state.currentIndex++;
      renderCard();
    } else {
      showSummary();
    }
  }

  function showSummary(): void {
    cardCleanups.forEach((fn) => fn());
    cardCleanups = [];

    clearElement(container);

    // Count mastery levels
    let masteredCount = 0;
    let learningCount = 0;
    let newCount = 0;
    for (const card of state.cards) {
      const progress = state.progressMap.get(card.id);
      if (!progress || progress.mastery === 'new') newCount++;
      else if (progress.mastery === 'learning') learningCount++;
      else masteredCount++;
    }

    const summaryDiv = createElement('div', {
      className: 'flashcard-study-wrapper flashcard-summary',
    });

    const heading = createElement('h2', {
      textContent: 'Deck Complete!',
      className: 'flashcard-summary-heading',
    });

    const sessionText = createElement('p', { className: 'text-secondary' });
    sessionText.textContent = `${state.sessionCorrect} known, ${state.sessionIncorrect} need review`;

    const statsDiv = createElement('div', { className: 'flashcard-summary-stats' });

    const masteredStat = createElement('div', { className: 'flashcard-stat' });
    const masteredNum = createElement('p', { className: 'flashcard-stat-number stat-mastered' });
    masteredNum.textContent = String(masteredCount);
    const masteredLabel = createElement('p', {
      className: 'flashcard-stat-label',
      textContent: 'Mastered',
    });
    masteredStat.append(masteredNum, masteredLabel);

    const learningStat = createElement('div', { className: 'flashcard-stat' });
    const learningNum = createElement('p', { className: 'flashcard-stat-number stat-learning' });
    learningNum.textContent = String(learningCount);
    const learningLabel = createElement('p', {
      className: 'flashcard-stat-label',
      textContent: 'Learning',
    });
    learningStat.append(learningNum, learningLabel);

    const newStat = createElement('div', { className: 'flashcard-stat' });
    const newNum = createElement('p', { className: 'flashcard-stat-number' });
    newNum.textContent = String(newCount);
    const newLabel = createElement('p', { className: 'flashcard-stat-label', textContent: 'New' });
    newStat.append(newNum, newLabel);

    statsDiv.append(masteredStat, learningStat, newStat);

    const actionsContainer = createElement('div', { className: 'flashcard-summary-actions' });

    const studyAgainBtn = createElement('button', {
      className: 'btn btn-primary',
      textContent: 'Study All Cards Again',
    });
    const studyAgainCleanup = addListener(studyAgainBtn, 'click', () => {
      clearElement(container);
      state.currentIndex = 0;
      state.sessionCorrect = 0;
      state.sessionIncorrect = 0;
      container.appendChild(wrapper);
      renderCard();
    });
    cleanups.push(studyAgainCleanup);

    actionsContainer.appendChild(studyAgainBtn);

    // Only show "Review Weak Cards" if there are non-mastered cards
    const weakCards = state.cards.filter((c) => {
      const p = state.progressMap.get(c.id);
      return !p || p.mastery !== 'mastered';
    });

    if (weakCards.length > 0 && weakCards.length < state.cards.length) {
      const reviewWeakBtn = createElement('button', {
        className: 'btn btn-secondary',
        textContent: `Review Weak Cards (${weakCards.length})`,
      });
      const reviewWeakCleanup = addListener(reviewWeakBtn, 'click', () => {
        clearElement(container);
        state.cards = weakCards;
        state.currentIndex = 0;
        state.sessionCorrect = 0;
        state.sessionIncorrect = 0;
        container.appendChild(wrapper);
        renderCard();
      });
      cleanups.push(reviewWeakCleanup);
      actionsContainer.appendChild(reviewWeakBtn);
    }

    const homeLink = createElement('a', { textContent: 'Go Home', className: 'btn btn-secondary' });
    homeLink.setAttribute('href', '#/');
    actionsContainer.appendChild(homeLink);

    summaryDiv.append(heading, sessionText, statsDiv, actionsContainer);
    container.appendChild(summaryDiv);
  }

  return () => cleanups.forEach((fn) => fn());
}
